/*
 * MapTab.jsx
 *
 * Leaflet map with marker cluster groups. Dynamically loads Leaflet and
 * MarkerCluster from CDN on first mount. Exposes flyToCharger() and clearPin()
 * to the parent via forwardRef / useImperativeHandle so the fleet grid can
 * programmatically pan to a selected charger.
 * Filter changes are synced via refs to avoid stale-closure issues in marker callbacks.
 * Imported by: Dashboard.jsx.
 */

import { useRef, useEffect, useState, forwardRef, useImperativeHandle } from "react";
import {
  LEAFLET_CSS, CLUSTER_CSS, LEAFLET_JS, CLUSTER_JS,
  loadCSS, loadScript,
  markerStyle, chargerMatchesFilter,
  API,
} from "./utils";

function dot(color) {
  return `<span style="display:inline-block;width:10px;height:10px;border-radius:50%;background:${color};margin-right:6px;vertical-align:middle"></span>`;
}

const MapTab = forwardRef(({ locations, onChargerSelect, activeFilter }, ref) => {
  const containerRef   = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef     = useRef({});
  const clusterRef     = useRef(null);
  const pinnedRef      = useRef(null);
  const onSelectRef    = useRef(onChargerSelect);
  const filterRef      = useRef(activeFilter);
  const chargersRef    = useRef({});
  const [ready, setReady] = useState(false);

  useEffect(() => { onSelectRef.current = onChargerSelect; }, [onChargerSelect]);
  useEffect(() => { filterRef.current = activeFilter; }, [activeFilter]);

  useImperativeHandle(ref, () => ({
    flyToCharger(charger_sn, lat, lon) {
      if (!mapInstanceRef.current) return;
      pinnedRef.current = { charger_sn, lat, lon };
      mapInstanceRef.current.flyTo([lat, lon], 15, { animate: true, duration: 1.0 });
      setTimeout(() => {
        markersRef.current[charger_sn]?.openPopup();
      }, 1100);
    },
    clearPin() {
      pinnedRef.current = null;
    },
  }));

  useEffect(() => {
    [LEAFLET_CSS, ...CLUSTER_CSS].forEach(loadCSS);
    loadScript(LEAFLET_JS, () => !!window.L)
      .then(() => loadScript(CLUSTER_JS, () => !!window.L?.markerClusterGroup))
      .then(() => setReady(true));
  }, []);

  // Init map exactly once when libs are ready (preserves user pan/zoom across fleet refreshes)
  useEffect(() => {
    if (!ready || !containerRef.current || mapInstanceRef.current) return;
    const L = window.L;
    try {
      const map = L.map(containerRef.current).setView([36.7, -119.4], 6);
      mapInstanceRef.current = map;

      L.tileLayer("https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}", {
        attribution: "Tiles &copy; Esri &mdash; Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community",
        maxZoom: 19,
      }).addTo(map);

      const legend = L.control({ position: "bottomright" });
      legend.onAdd = () => {
        const div = L.DomUtil.create("div", "dp-map-legend");
        div.innerHTML = [
          "<div class='dp-map-legend-title'>PRIORITY</div>",
          dot("#EF4444") + "HIGH — Confirmed fault",
          dot("#F97316") + "MEDIUM — Schedule today",
          dot("#F59E0B") + "LOW — Monitor",
          dot("#0EA5E9") + "Healthy",
          dot("#8B5CF6") + "Early warning",
          dot("#9CA3AF") + "Muted",
        ].join("<br>");
        return div;
      };
      legend.addTo(map);
    } catch (e) {
      console.error("[MapTab] init error:", e);
    }

    return () => {
      try {
        if (mapInstanceRef.current) { mapInstanceRef.current.remove(); mapInstanceRef.current = null; }
        if (containerRef.current) delete containerRef.current._leaflet_id;
        markersRef.current = {};
        clusterRef.current = null;
      } catch { }
    };
  }, [ready]);

  // Sync markers when locations change — does not destroy the map, so view is preserved
  useEffect(() => {
    if (!ready || !mapInstanceRef.current || !locations) return;
    const L = window.L;
    const map = mapInstanceRef.current;

    if (clusterRef.current) {
      map.removeLayer(clusterRef.current);
      clusterRef.current = null;
      markersRef.current = {};
    }

    const cluster = L.markerClusterGroup({
      maxClusterRadius: 1,
      zoomToBoundsOnClick: false,
      spiderfyOnMaxZoom: true,
      showCoverageOnHover: false,
      iconCreateFunction: (cl) => {
        const children = cl.getAllChildMarkers();
        const filter   = filterRef.current;
        const matching = children.filter(m => {
          const c = chargersRef.current[m.options._sn];
          return c && chargerMatchesFilter(c, filter);
        });
        const total  = children.length;
        const shown  = filter === "ALL" ? total : matching.length;
        const dimmed = shown === 0;
        const sample = (matching[0] || children[0]);
        const c      = sample ? chargersRef.current[sample.options._sn] : null;
        const color  = c ? markerStyle(c).color : "#6B7280";
        return L.divIcon({
          html: `<div class="dp-cluster-badge${dimmed ? " dp-cluster-dimmed" : ""}" style="--cluster-color:${color}">${shown}</div>`,
          className: "dp-cluster",
          iconSize: [22, 22],
        });
      },
    });
    clusterRef.current = cluster;
    cluster.on("clusterclick", (e) => { e.layer.spiderfy(); });

    chargersRef.current = {};
    locations.forEach(c => {
      if (!c.latitude || !c.longitude) return;
      chargersRef.current[c.charger_sn] = c;
      const { color, radius, className } = markerStyle(c);
      const marker = L.circleMarker([c.latitude, c.longitude], {
        radius, fillColor: color, color: "#fff",
        weight: 2, opacity: 1, fillOpacity: 0.92,
        className,
        _sn: c.charger_sn,
      });

      const primaryId = (c.station_id && c.station_id !== "—") ? c.station_id : c.charger_sn;
      const errLine   = c.error_info?.description
        ? `<div style="margin-top:4px;font-size:11px;color:#6B7280">${c.error_info.description}</div>` : "";
      const siteLine  = c.site_name
        ? `<div style="font-size:12px;color:#374151;margin-bottom:1px">${c.site_name}</div>` : "";
      const snLine    = (c.station_id && c.station_id !== "—")
        ? `<div style="font-size:10px;color:#9CA3AF;font-family:monospace">${c.charger_sn} · ${c.station_type || ""}</div>` : "";

      marker.bindPopup(`
        <div style="font-family:sans-serif;min-width:200px">
          <div style="font-weight:700;font-size:14px;color:#1E3A8A;margin-bottom:3px">${primaryId}</div>
          ${siteLine}${snLine}
          <div style="margin-top:6px;font-size:12px">
            <span style="font-weight:600;color:${color}">${c.priority_tier}</span>
            &nbsp;·&nbsp;${((c.fault_probability || 0) * 100).toFixed(1)}% fault prob
          </div>
          ${errLine}
          <div style="margin-top:8px">
            <a href="#" data-sn="${c.charger_sn}"
               style="font-size:11px;color:#1D4ED8;text-decoration:none;font-weight:600">
              View details →
            </a>
          </div>
        </div>
      `);

      marker.on("popupopen", () => {
        setTimeout(() => {
          const a = document.querySelector(`a[data-sn="${c.charger_sn}"]`);
          if (a) a.onclick = e => { e.preventDefault(); onSelectRef.current?.(c); };
        }, 50);
        fetch(`${API}/charger/${c.charger_sn}/details`)
          .then(r => r.ok ? r.json() : null)
          .then(d => {
            if (!d || !marker.isPopupOpen()) return;
            const rev30       = d.revenue_30d;
            const revStr      = rev30?.sessions > 0
              ? `<div style="margin-top:4px;font-size:11px;color:#16A34A;font-weight:600">$${(rev30.revenue_usd || 0).toFixed(0)} / 30d · ${rev30.sessions} sessions</div>`
              : "";
            const detailSn     = (d.station_id && d.station_id !== "—") ? d.station_id : c.charger_sn;
            const addrLine     = d.address ? `<div style="font-size:10px;color:#9CA3AF;margin-bottom:2px">${d.address}</div>` : "";
            const connRow      = d.connector_types?.length
              ? `<div style="font-size:10px;color:#9CA3AF">${d.connector_types.join(" · ")}</div>` : "";
            const detailSnLine = (d.station_id && d.station_id !== "—")
              ? `<div style="font-size:10px;color:#9CA3AF;font-family:monospace">${c.charger_sn} · ${c.station_type || ""}</div>` : "";
            marker.setPopupContent(`
              <div style="font-family:sans-serif;min-width:200px">
                <div style="font-weight:700;font-size:14px;color:#1E3A8A;margin-bottom:3px">${detailSn}</div>
                ${siteLine}${addrLine}${detailSnLine}${connRow}
                <div style="margin-top:6px;font-size:12px">
                  <span style="font-weight:600;color:${color}">${c.priority_tier}</span>
                  &nbsp;·&nbsp;${((c.fault_probability || 0) * 100).toFixed(1)}% fault prob
                </div>
                ${errLine}${revStr}
                <div style="margin-top:8px">
                  <a href="#" data-sn="${c.charger_sn}"
                     style="font-size:11px;color:#1D4ED8;text-decoration:none;font-weight:600">
                    View details →
                  </a>
                </div>
              </div>
            `);
            setTimeout(() => {
              const a = document.querySelector(`a[data-sn="${c.charger_sn}"]`);
              if (a) a.onclick = e => { e.preventDefault(); onSelectRef.current?.(c); };
            }, 50);
          })
          .catch(() => { });
      });

      markersRef.current[c.charger_sn] = marker;
      cluster.addLayer(marker);
    });

    map.addLayer(cluster);

    if (pinnedRef.current) {
      const { charger_sn: psn } = pinnedRef.current;
      setTimeout(() => { markersRef.current[psn]?.openPopup(); }, 100);
    }
  }, [ready, locations]);

  useEffect(() => {
    if (!ready || !locations?.length) return;
    Object.entries(markersRef.current).forEach(([sn, marker]) => {
      const c = chargersRef.current[sn] || locations.find(l => l.charger_sn === sn);
      if (!c) return;
      const matches = chargerMatchesFilter(c, activeFilter);
      const { radius } = markerStyle(c);
      marker.setStyle({ fillOpacity: matches ? 0.92 : 0.12, opacity: matches ? 1 : 0.25 });
      marker.setRadius(matches ? radius : 4);
    });
    if (clusterRef.current?.refreshClusters) clusterRef.current.refreshClusters();
  }, [activeFilter, ready, locations]);

  return (
    <div style={{ position: "relative" }}>
      {!ready && (
        <div style={{ height: 560, display: "flex", alignItems: "center", justifyContent: "center", color: "#9CA3AF", fontSize: 13 }}>
          Loading map…
        </div>
      )}
      <div ref={containerRef} className="dp-map" style={{ height: 560, borderRadius: 8, display: ready ? "block" : "none", border: "1px solid #E5E7EB" }} />
    </div>
  );
});

export default MapTab;
